/**
 * Hierarchical sorting for cards and linkable items.
 *
 * All sort functions in this module use a shared bucket system (HIERARCHY_BUCKETS)
 * so that cards and linkable items are always ordered consistently:
 *
 *   1. ART work items  — user's team first, then others
 *   2. Dependencies    — user depend-team, then precond-team, then rest
 *   3. Team work items
 *   4. Objectives      — committed, then uncommitted  (LinkableItem only)
 *   5. Everything else
 *
 * Within each bucket, items are sorted by iteration ID then by name.
 *
 * Exports:
 *   - sortCardsHierarchically          — sorts Card[] into hierarchy buckets
 *   - sortLinkableItems             — sorts LinkableItem[] (cards + objectives)
 *   - sortCardsByIterationAndName   — sorts Card[] by iteration then name
 */
import { sortBy } from "lodash-es";

import type { Card } from "@/model/card";
import type { LinkableCard, LinkableItem } from "@/model/link";

import { useUserStore } from "../user";
import { isARTOrigin } from "./linkableItems";

// ─── Public sort functions ───────────────────────────────────────────

/** Sort cards by iteration ID then by name, placing items without iterations at the end. */
export function sortCardsByIterationAndName<T extends Card>(cards: T[]): T[] {
  return sortBy(cards, [(card) => card.iterationId ?? END, (card) => card.text]);
}

/** Sort cards by hierarchy bucket with user-team prioritization, then by iteration and name. */
export function sortCardsHierarchically<T extends Card>(cards: T[]): T[] {
  return sortBy(cards, [
    (card) => bucketIndex(classifyCard(card)),
    (card) => card.iterationId ?? END,
    (card) => card.text,
  ]);
}

/** Sort linkable items (cards + objectives) by hierarchy, falling back to iteration and text. */
export function sortLinkableItems(items: LinkableItem[]): LinkableItem[] {
  return sortBy(items, [
    (item) => bucketIndex(classifyLinkableItem(item)),
    (item) => (item.kind === "sticky" ? (item.iteration?.id ?? END) : END),
    (item) => item.text,
  ]);
}

// ─── Bucket definitions ─────────────────────────────────────────────

/**
 * Canonical bucket order for all hierarchy-based sorting.
 * Changing this array changes the sort order everywhere.
 */
const HIERARCHY_BUCKETS = [
  "art:userTeam",
  "art:other",
  "dep:userDepend",
  "dep:userPrecond",
  "dep:other",
  "teamWork",
  "objective:committed",
  "objective:uncommitted",
  "other",
] as const;

type HierarchyBucket = (typeof HIERARCHY_BUCKETS)[number];

// ─── Classifiers ─────────────────────────────────────────────────────
// Each classifier maps an item to one of the HIERARCHY_BUCKETS above.
// User-team affinity determines sub-bucket placement within ART and dependency groups.]

function classifyCard(card: LinkableCard | Card): HierarchyBucket {
  if (card.type.functionality === "dependency") {
    if (card.dependTeam && isUserTeam(card.dependTeam.id)) return "dep:userDepend";
    if (card.precondTeam && isUserTeam(card.precondTeam.id)) return "dep:userPrecond";
    return "dep:other";
  }
  if (isARTOrigin(card.type.origin)) {
    // Card has teamId: string | null; LinkableCard has team?: Team
    const teamId = "teamId" in card ? card.teamId : card.team?.id;
    return teamId && isUserTeam(teamId) ? "art:userTeam" : "art:other";
  }
  if (card.type.origin === "team") return "teamWork";
  return "other";
}

function classifyLinkableItem(item: LinkableItem): HierarchyBucket {
  switch (item.kind) {
    case "objective":
      return item.type === "committed" ? "objective:committed" : "objective:uncommitted";
    case "sticky":
      return classifyCard(item);
  }
}

// ─── Sort helpers ────────────────────────────────────────────────────

const END = Number.MAX_SAFE_INTEGER;

function bucketIndex(bucket: HierarchyBucket): number {
  return HIERARCHY_BUCKETS.indexOf(bucket);
}

function isUserTeam(teamId: string): boolean {
  return useUserStore().technicalUser.teams.some((t) => t.id === teamId);
}
